import {
  tidyUpSequenceData,
  condensePairwiseAlignmentDifferences
} from "@teselagen/sequence-utils";
import { convertBasePosTraceToPerBpTrace } from "@teselagen/bio-parsers";
import shortid from "shortid";
import addDashesForMatchStartAndEndForTracks from "./utils/addDashesForMatchStartAndEndForTracks";
import { createAction } from "redux-act";
import { omit } from "lodash-es";
const alignmentAnnotationSettings = {
  axis: true,
  axisNumbers: true,
  chromatogram: true,
  dnaColors: false,
  features: false,
  parts: false,
  reverseSequence: false,
  sequence: true,
  translations: true,
  orfs: false,
  orfTranslations: false,
  cdsFeatureTranslations: false,
  cutsites: false,
  primers: true,
  compactNames: false
};
const defaultVisibilities = {
  alignmentAnnotationVisibility: alignmentAnnotationSettings,
  pairwise_alignmentAnnotationVisibility: alignmentAnnotationSettings,
  alignmentAnnotationLabelVisibility: {
    features: false,
    parts: false,
    cutsites: false,
    primers: true
  },
  pairwise_alignmentAnnotationLabelVisibility: {
    features: false,
    parts: false,
    cutsites: false,
    primers: true
  }
};
const defaultVisibilityTypes = Object.keys(defaultVisibilities);
try {
  defaultVisibilityTypes.forEach((type) => {
    const newVal = JSON.parse(window.localStorage.getItem(type));
    if (newVal)
      defaultVisibilities[type] = {
        ...defaultVisibilities[type],
        ...newVal
      };
  });
} catch (e) {
  console.error("error setting localstorage visibility config", e);
}
export const upsertAlignmentRun = createAction("UPSERT_ALIGNMENT_RUN");
export const removeAlignmentFromRedux = createAction(
  "REMOVE_ALIGNMENT_FROM_REDUX"
);
export const updateAlignmentViewVisibility = createAction(
  "UPDATE_ALIGNMENT_VIEW_VISIBILITY"
);
export const alignmentRunUpdate = createAction("ALIGNMENT_RUN_UPDATE");
const highlightRangeProps = {
  color: "red",
  hideCarets: true,
  ignoreGaps: true
};
function addHighlightedDifferences(alignmentTracks) {
  return alignmentTracks.map((track) => {
    if (track.isUnmapped) {
      return track;
    }
    const sequenceData = tidyUpSequenceData(track.sequenceData, {
      doNotRemoveInvalidChars: true
    });
    const matchHighlightRanges = getRangeMatchesBetweenTemplateAndNonTemplate(
      alignmentTracks[0].alignmentData.sequence,
      track.alignmentData.sequence
    );
    const mismatches = matchHighlightRanges.filter(({ isMatch }) => !isMatch);
    return {
      ...track,
      sequenceData,
      matchHighlightRanges,
      additionalSelectionLayers: matchHighlightRanges.filter(({ isMatch }) => !isMatch).map((range) => {
        return {
          ...range,
          ...highlightRangeProps,
          className: "veAlignmentMismatch"
        };
      }),
      mismatches
    };
  });
}
export default (state = {}, { payload = {}, type }) => {
  if (type === "ALIGNMENT_RUN_UPDATE") {
    const { alignmentId } = payload;
    const newState = {
      ...state,
      [alignmentId]: {
        ...state[alignmentId],
        ...payload
      }
    };
    return newState;
  }
  if (type === "UPDATE_ALIGNMENT_VIEW_VISIBILITY") {
    defaultVisibilityTypes.forEach((type2) => {
      if (type2.startsWith("pairwise_") && payload.pairwiseAlignments || !type2.startsWith("pairwise_") && !payload.pairwiseAlignments) {
        defaultVisibilities[type2] = {
          ...defaultVisibilities[type2],
          ...payload[type2.replace("pairwise_", "")]
        };
        localStorage.setItem(
          type2,
          JSON.stringify({
            ...defaultVisibilities[type2],
            ...payload[type2.replace("pairwise_", "")]
          })
        );
      }
    });
    return {
      ...state,
      [payload.id]: { ...payload }
    };
  }
  if (type === "UPSERT_ALIGNMENT_RUN") {
    const { id } = payload;
    const oldState = state[id] || {};
    const payloadToUse = {
      ...oldState,
      stateTrackingId: payload.isInitialLoad ? "initialLoadId" : oldState.stateTrackingId ? shortid() : "initialLoadId",
      alignmentType: oldState.alignmentType || payload.alignmentType,
      ...payload,
      //assign default visibilities
      ...defaultVisibilityTypes.reduce((acc, type2) => {
        const key = type2.replace("pairwise_", "");
        if (type2.startsWith("pairwise_") && payload.pairwiseAlignments || !type2.startsWith("pairwise_") && !payload.pairwiseAlignments) {
          acc[key] = {
            ...defaultVisibilities[type2],
            ...state[id] && state[id][key],
            ...payload[key]
          };
        }
        return acc;
      }, {})
    };
    if (payloadToUse.pairwiseAlignments && payloadToUse.pairwiseAlignments.length > 0) {
      if (payloadToUse.pairwiseAlignments[0][0] && payloadToUse.pairwiseAlignments[0][0].alignmentData && payloadToUse.pairwiseAlignments[0][0].alignmentData.matchStart !== void 0) {
        payloadToUse.pairwiseAlignments = payloadToUse.pairwiseAlignments.map(
          addDashesForMatchStartAndEndForTracks
        );
      }
      const templateSeq = payloadToUse.pairwiseAlignments[0][0];
      const pairwiseOverviewAlignmentTracks = [
        {
          //add the template seq as the first track in the Pairwise Alignment Overview
          ...templateSeq,
          sequenceData: tidyUpSequenceData(templateSeq.sequenceData, {
            doNotRemoveInvalidChars: true
          }),
          alignmentData: { sequence: templateSeq.sequenceData.sequence }
          //remove the gaps from the template sequence
        }
      ];
      payloadToUse.pairwiseAlignments.forEach(([template, alignedSeq]) => {
        const condensedSeq = condensePairwiseAlignmentDifferences(
          template.alignmentData.sequence,
          alignedSeq.alignmentData.sequence
        );
        let re = /r+/gi;
        let match;
        const additionalSelectionLayers = [];
        while ((match = re.exec(condensedSeq)) != null) {
          additionalSelectionLayers.push({
            start: match.index,
            end: match.index + match[0].length - 1,
            ...highlightRangeProps,
            className: "veAlignmentMismatch"
          });
        }
        re = /g+/gi;
        while ((match = re.exec(condensedSeq)) != null) {
          additionalSelectionLayers.push({
            start: match.index,
            end: match.index + match[0].length - 1,
            ...highlightRangeProps,
            color: "grey"
          });
        }
        const alignedSeqMinusInserts = {
          ...alignedSeq,
          sequenceData: {
            ...tidyUpSequenceData(alignedSeq.sequenceData, {
              doNotRemoveInvalidChars: true
            }),
            sequence: template.sequenceData.sequence
          },
          additionalSelectionLayers,
          alignmentData: {
            sequence: condensedSeq
          }
        };
        pairwiseOverviewAlignmentTracks.push(alignedSeqMinusInserts);
      });
      payloadToUse.pairwiseOverviewAlignmentTracks = pairwiseOverviewAlignmentTracks;
      payloadToUse.pairwiseAlignments = payloadToUse.pairwiseAlignments.map(
        addHighlightedDifferences
      );
    }
    if (payloadToUse.alignmentTracks) {
      payloadToUse.alignmentTracks = addHighlightedDifferences(
        payloadToUse.alignmentTracks
      );
    }
    let hasError = checkForIssues(
      payloadToUse.alignmentTracks,
      payloadToUse.alignmentType
    );
    (payloadToUse.pairwiseAlignments || []).forEach((alignment) => {
      const error = checkForIssues(alignment, payloadToUse.alignmentType);
      if (error) {
        hasError = error;
      }
    });
    return {
      ...state,
      [payload.id]: {
        ...payloadToUse,
        hasError
      }
    };
  }
  if (type === "REMOVE_ALIGNMENT_FROM_REDUX") {
    const { id } = payload;
    state = omit(state, [id]);
  }
  return state;
};
function getRangeMatchesBetweenTemplateAndNonTemplate(tempSeq, nonTempSeq) {
  const seqLength = nonTempSeq.length;
  const ranges = [];
  const nonTempSeqWithoutLeadingDashes = nonTempSeq.replace(/^-+/g, "");
  const nonTempSeqWithoutTrailingDashes = nonTempSeq.replace(/-+$/g, "");
  const startIndex = seqLength - nonTempSeqWithoutLeadingDashes.length;
  const endIndex = seqLength - (seqLength - nonTempSeqWithoutTrailingDashes.length);
  for (let index = startIndex; index < endIndex; index++) {
    const tempChar = tempSeq[index];
    const nonTempChar = nonTempSeq[index];
    const isMatch = tempChar && nonTempChar && tempChar.toLowerCase() === nonTempChar.toLowerCase();
    const previousRange = ranges[ranges.length - 1];
    if (previousRange) {
      if (previousRange.isMatch === isMatch) {
        previousRange.end++;
      } else {
        ranges.push({
          start: index,
          end: index,
          isMatch
        });
      }
    } else {
      ranges.push({
        start: startIndex,
        end: startIndex,
        isMatch
      });
    }
  }
  return ranges;
}
function checkForIssues(alignmentTracks, alignmentType) {
  if (!alignmentTracks || !alignmentTracks[0] || !alignmentTracks[0].alignmentData) {
    return;
  }
  const alignmentTrackLength = alignmentTracks[0].alignmentData.sequence.length;
  let hasError;
  alignmentTracks.some((track) => {
    if (track.alignmentData.sequence.length !== alignmentTrackLength) {
      console.error("incorrect length", alignmentTracks);
      return "incorrect length";
    }
    if (track.chromatogramData && track.sequenceData.sequence.length !== track.chromatogramData.baseCalls.length) {
      console.error("incorrect chromatogram length", alignmentTracks);
      return "incorrect chromatogram length";
    }
    if (track.chromatogramData && !track.chromatogramData.baseTraces) {
      if (!track.chromatogramData.basePos) {
        console.error("corrupted chromatogram data", alignmentTracks);
        return "corrupted chromatogram data";
      }
      track.chromatogramData = convertBasePosTraceToPerBpTrace(
        track.chromatogramData
      );
    }
    if (alignmentType !== "Parallel Part Creation" && track.sequenceData.sequence.length !== track.alignmentData.sequence.replace(/-/g, "").length) {
      console.warn(
        "sequence data length does not match alignment data w/o gaps"
      );
      console.warn(
        "track.sequenceData.sequence.length:",
        track.sequenceData.sequence.length
      );
      console.warn(
        'track.alignmentData.sequence.replace(/-/g,"").length:',
        track.alignmentData.sequence.replace(/-/g, "").length
      );
    }
    return false;
  });
  if (hasError) {
    return hasError;
  }
}
